// Inline tasks embedded in a Note (tasks/frontend-inline-tasks-phase-1-requirements.md).
//
// A task lives in the Note's description as an `inlineTask` editor block
// (<div data-type="inline-task" data-task-id=… …>description</div>), so it is saved, synced and
// conflict-resolved exactly like the rest of the Note's content. `Note.tasks` is derived from that
// HTML on save (extractInlineTasksFromHtml + mergeInlineTasks), keeping any extra fields the server
// returned for a task (e.g. `comments`, which Phase 1 doesn't show).

export interface InlineTask {
  id: string;
  description: string;
  completed: boolean;
  ownerId?: string;
  /** Date only, `YYYY-MM-DD`. */
  dueDate?: string;
  completedAt?: string;
  createdAt: string;
  modifiedAt: string;
  /** Server-only in Phase 1: preserved, never shown or edited. */
  comments?: string;
  [extra: string]: unknown;
}

/** Someone a task can be assigned to: a member of the Note's Space. */
export interface TaskAssignee {
  id: string;
  name: string;
}

export const INLINE_TASK_SELECTOR = 'div[data-type="inline-task"]';

export function newInlineTaskId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `task-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** A note's tasks; a note without `tasks` has none. */
export function noteTasks(note: { tasks?: unknown } | null | undefined): InlineTask[] {
  return Array.isArray(note?.tasks) ? (note!.tasks as InlineTask[]) : [];
}

// --- Due dates: stored date-only (YYYY-MM-DD), compared and shown in the user's local time zone ---

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DD` of a Date in local time. */
export function toLocalDateOnly(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A real calendar date in `YYYY-MM-DD` form (rejects e.g. 2026-02-30). */
export function isValidTaskDueDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = DATE_ONLY.exec(value);
  if (!m) return false;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return toLocalDateOnly(date) === value;
}

/** A picker Date / stored string as a due date; null when empty or not a valid date. */
export function toTaskDueDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? toLocalDateOnly(value) : null;
  if (typeof value !== 'string') return null;
  if (isValidTaskDueDate(value)) return value;
  const time = Date.parse(value);
  return Number.isFinite(time) ? toLocalDateOnly(new Date(time)) : null;
}

/** Picker value (local midnight) for a stored due date; null when empty/invalid. */
export function taskDueDateToDate(value: unknown): Date | null {
  if (!isValidTaskDueDate(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

/** Past its due date and still open. */
export function isTaskOverdue(task: { dueDate?: string | null; completed?: boolean }, today: Date = new Date()): boolean {
  return !task.completed && isValidTaskDueDate(task.dueDate) && task.dueDate < toLocalDateOnly(today);
}

/** "Today", "Tomorrow", "Yesterday", else a short local date ("5 Oct", or "5 Oct 2027" in another year). */
export function formatTaskDueDate(value: unknown, today: Date = new Date()): string {
  const date = taskDueDateToDate(value);
  if (!date) return 'Invalid date';
  const iso = value as string;
  if (iso === toLocalDateOnly(today)) return 'Today';
  if (iso === toLocalDateOnly(addDays(today, 1))) return 'Tomorrow';
  if (iso === toLocalDateOnly(addDays(today, -1))) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** Long form for screen readers/tooltips ("Monday, 5 October 2026"). */
export function formatTaskDueDateLong(value: unknown): string {
  const date = taskDueDateToDate(value);
  return date
    ? date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : 'an invalid date';
}

// --- Owners ---

/** The owner's display name; a short id when they aren't in the (possibly stale) member list. */
export function taskOwnerName(ownerId: string | null | undefined, assignees: readonly TaskAssignee[] | null): string {
  if (!ownerId) return '';
  const found = assignees?.find((a) => a.id === ownerId);
  return found?.name || `User ${ownerId.length > 8 ? ownerId.slice(0, 8) + '…' : ownerId}`;
}

/** An owner is eligible when they're a member of the Note's Space. Unknown membership (null — e.g.
 * offline on a device that never loaded it) never flags an owner as ineligible. */
export function isEligibleTaskOwner(ownerId: string | null | undefined, assignees: readonly TaskAssignee[] | null): boolean {
  if (!ownerId || assignees === null) return true;
  return assignees.some((a) => a.id === ownerId);
}

// --- Extraction from the Note's HTML, and merge with what the Note already had ---

function attr(el: Element, name: string): string | undefined {
  const value = el.getAttribute(name);
  return value === null || value === '' ? undefined : value;
}

/** The tasks embedded in a description, in document order. */
export function extractInlineTasksFromHtml(html: string | null | undefined, now: string = new Date().toISOString()): InlineTask[] {
  if (!html || !html.includes('inline-task')) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll(INLINE_TASK_SELECTOR)).map((el) => {
    const createdAt = attr(el, 'data-created-at') ?? now;
    const task: InlineTask = {
      id: attr(el, 'data-task-id') ?? newInlineTaskId(),
      description: (el.textContent ?? '').replace(/\s+/g, ' ').trim(),
      completed: el.getAttribute('data-completed') === 'true',
      createdAt,
      modifiedAt: attr(el, 'data-modified-at') ?? createdAt,
    };
    const ownerId = attr(el, 'data-owner-id');
    if (ownerId) task.ownerId = ownerId;
    const dueDate = attr(el, 'data-due-date');
    if (dueDate) task.dueDate = dueDate;
    const completedAt = attr(el, 'data-completed-at');
    if (task.completed && completedAt) task.completedAt = completedAt;
    return task;
  });
}

const EDITOR_OWNED_FIELDS = ['id', 'description', 'completed', 'ownerId', 'dueDate', 'completedAt', 'createdAt', 'modifiedAt'];

/** `current` (from the editor) with any other fields the same task had in `previous` kept — so
 * server-only fields such as `comments` survive an edit. Tasks no longer in `current` are gone. */
export function mergeInlineTasks(current: InlineTask[], previous: readonly InlineTask[] | null | undefined): InlineTask[] {
  const byId = new Map((previous ?? []).filter((t) => t && typeof t.id === 'string').map((t) => [t.id, t]));
  return current.map((task) => {
    const before = byId.get(task.id);
    if (!before) return task;
    const kept: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(before)) {
      if (!EDITOR_OWNED_FIELDS.includes(key)) kept[key] = value;
    }
    return { ...kept, ...task } as InlineTask;
  });
}

// --- Validation ---

export type InlineTaskIssueField = 'description' | 'ownerId' | 'dueDate';

export interface InlineTaskIssue {
  taskId: string;
  field: InlineTaskIssueField;
  message: string;
}

/** Frontend rules: a description, a valid due date (or none), and an owner from the eligible
 * assignees (or none). */
export function validateInlineTasks(
  tasks: readonly Pick<InlineTask, 'id' | 'description' | 'ownerId' | 'dueDate'>[],
  assignees: readonly TaskAssignee[] | null,
): InlineTaskIssue[] {
  const issues: InlineTaskIssue[] = [];
  for (const task of tasks) {
    if (!task.description?.trim()) {
      issues.push({ taskId: task.id, field: 'description', message: 'A task needs a description — type one or delete the task.' });
    }
    if (task.dueDate && !isValidTaskDueDate(task.dueDate)) {
      issues.push({ taskId: task.id, field: 'dueDate', message: 'A task has an invalid due date — pick a new one or clear it.' });
    }
    if (!isEligibleTaskOwner(task.ownerId, assignees)) {
      issues.push({
        taskId: task.id,
        field: 'ownerId',
        message: `${taskOwnerName(task.ownerId, assignees)} no longer has access to this note — pick another owner or clear it.`,
      });
    }
  }
  return issues;
}

// --- Reading a note's tasks for lists (Home Tasks view, task search) ---

const tasksByNote = new Map<string, { html: string; tasks: InlineTask[] }>();

/** The tasks in a note's description, in document order. The HTML is the source of truth (it is
 * what syncs), so this reads it rather than `Note.tasks`; parsed once per note per HTML version. */
export function noteInlineTasks(note: { _id?: string; DescriptionHtml?: string | null } | null | undefined): InlineTask[] {
  const html = note?.DescriptionHtml ?? '';
  if (!html.includes('inline-task')) return [];
  const key = note?._id ?? '';
  const cached = key ? tasksByNote.get(key) : undefined;
  if (cached && cached.html === html) return cached.tasks;
  const tasks = extractInlineTasksFromHtml(html);
  if (key) tasksByNote.set(key, { html, tasks });
  return tasks;
}

// --- Changing tasks outside the editor (Home Tasks view: Completed toggle, Select-mode bulk actions) ---

/** A change to one task: new attribute values, or removal. */
export interface InlineTaskChange {
  completed?: boolean;
  /** null clears the owner. */
  ownerId?: string | null;
  /** `YYYY-MM-DD`; null clears the due date. */
  dueDate?: string | null;
  delete?: boolean;
}

function setOrRemove(el: Element, name: string, value: string | null | undefined): void {
  if (value) el.setAttribute(name, value);
  else el.removeAttribute(name);
}

/** The description HTML with the given tasks changed (by task id) the way the editor would change
 * them: modified time bumped, completed time set/cleared, a deleted task's block removed (an empty
 * container gets an empty line, so the document stays valid). Tasks not in `changes` are untouched.
 * Returns the HTML unchanged when none of the tasks is found. */
export function applyInlineTaskChanges(
  html: string | null | undefined,
  changes: ReadonlyMap<string, InlineTaskChange>,
  now: string = new Date().toISOString(),
): string {
  const source = html ?? '';
  if (!changes.size || !source.includes('inline-task')) return source;
  const doc = new DOMParser().parseFromString(source, 'text/html');
  let changed = false;
  for (const el of Array.from(doc.querySelectorAll(INLINE_TASK_SELECTOR))) {
    const change = changes.get(el.getAttribute('data-task-id') ?? '');
    if (!change) continue;
    changed = true;
    if (change.delete) {
      const parent = el.parentElement;
      el.remove();
      if (parent && parent !== doc.body && !parent.children.length && !parent.textContent?.trim()) {
        parent.appendChild(doc.createElement('p'));
      }
      continue;
    }
    if (change.completed !== undefined) {
      const wasCompleted = el.getAttribute('data-completed') === 'true';
      el.setAttribute('data-completed', change.completed ? 'true' : 'false');
      if (change.completed && !wasCompleted) el.setAttribute('data-completed-at', now);
      if (!change.completed) el.removeAttribute('data-completed-at');
    }
    if (change.ownerId !== undefined) setOrRemove(el, 'data-owner-id', change.ownerId);
    if (change.dueDate !== undefined) setOrRemove(el, 'data-due-date', change.dueDate);
    el.setAttribute('data-modified-at', now);
  }
  if (!changed) return source;
  return doc.body.innerHTML || '<p></p>';
}

// --- Due-date filter (Home Tasks view) ---

export type TaskDueFilter = 'any' | 'overdue' | 'today' | 'tomorrow' | 'week';

/** Monday of the week `date` is in (weeks run Monday–Sunday). */
export function startOfWeek(date: Date): Date {
  const day = (date.getDay() + 6) % 7; // Monday = 0
  return addDays(date, -day);
}

/** Whether a task passes the due-date filter. Every filter except "any" leaves out tasks without a
 * (valid) due date. "week" is the current calendar week, Monday to Sunday. */
export function taskMatchesDueFilter(
  task: { dueDate?: string | null; completed?: boolean },
  filter: TaskDueFilter,
  today: Date = new Date(),
): boolean {
  if (filter === 'any') return true;
  if (!isValidTaskDueDate(task.dueDate)) return false;
  const due = task.dueDate;
  switch (filter) {
    case 'overdue':
      return isTaskOverdue(task, today);
    case 'today':
      return due === toLocalDateOnly(today);
    case 'tomorrow':
      return due === toLocalDateOnly(addDays(today, 1));
    case 'week': {
      const monday = startOfWeek(today);
      return due >= toLocalDateOnly(monday) && due <= toLocalDateOnly(addDays(monday, 6));
    }
  }
}
